import React, { createContext, useContext, useState, useCallback } from "react";
// Role-based permissions map
export const ROLE_PERMISSIONS = {
  superadmin: [
    "dashboard",
    "orders",
    "addProduct",
    "productList",
    "stockManagement",
    "manageSale",
    "manageBrands",
    "contentManagement",
    "manageShipping",
  ],
  pharmacist: [
    "dashboard",
    "orders",
    "addProduct",
    "productList",
    "stockManagement",
  ],
  inventory: [
    "dashboard",
    "addProduct",
    "productList",
    "stockManagement",
  ],
  cashier: [
    "dashboard",
    "orders",
  ],
};

// Route → permission key mapping
export const ROUTE_PERMISSION_MAP = {
  "/admin": "dashboard",
  "/admin/orders": "orders",
  "/admin/AddProduct": "addProduct",
  "/admin/ProductList": "productList",
  "/admin/StockManagement": "stockManagement",
  "/admin/BulkAddProduct": "stockManagement",
  "/admin/ManageSale": "manageSale",
  "/admin/ManageBrands": "manageBrands",
  "/admin/ContentManagement": "contentManagement",
  "/admin/ManageShipping": "manageShipping",
};

export const ROLE_LABELS = {
  superadmin: "Super Admin",
  pharmacist: "Pharmacist",
  inventory: "Inventory",
  cashier: "Cashier",
};

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const getStoredUser = () => {
    const auth = localStorage.getItem("adminAuth");
    if (!auth) return null;
    return {
      username: localStorage.getItem("adminUsername") || "",
      role: localStorage.getItem("adminRole") || "superadmin",
      name: localStorage.getItem("adminName") || "Admin",
    };
  };

  const [user, setUser] = useState(getStoredUser);

  const login = useCallback((userData) => {
    localStorage.setItem("adminAuth", "true");
    localStorage.setItem("adminRole", userData.role);
    localStorage.setItem("adminUsername", userData.username);
    localStorage.setItem("adminName", userData.name);
    setUser(userData);
  }, []);

  const logout = useCallback((navigate) => {
    localStorage.removeItem("adminAuth");
    localStorage.removeItem("adminRole");
    localStorage.removeItem("adminUsername");
    localStorage.removeItem("adminName");
    setUser(null);
    if (navigate) navigate("/login");
  }, []);

  const hasPermission = useCallback(
    (permission) => {
      if (!user) return false;
      const perms = ROLE_PERMISSIONS[user.role] || [];
      return perms.includes(permission);
    },
    [user]
  );

  return (
    <AuthContext.Provider value={{ user, login, logout, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
